import { KILL_CREDIT_WINDOW_MS } from '../physics/constants';
import { computeKnockback } from '../physics/knockback';
import { segmentCircleToi } from '../physics/obstacles';
import type { Vec2 } from '../physics/vec2';
import type { SpellId } from '../types';

/**
 * Earliest t in [0, 1] at which a projectile of radius `r` moving p0 → p1 this tick touches a
 * warlock of radius `R` at `target`, or null if it doesn't. Swept, so a fast shot (a 1400 u/s
 * Thrust moves ~47 u per tick) can't step over a target between ticks. Starting in contact is 0.
 */
export function sweptCircleHit(
  p0: Vec2,
  p1: Vec2,
  r: number,
  target: Vec2,
  R: number,
): number | null {
  return segmentCircleToi(p0.x, p0.y, p1.x, p1.y, target.x, target.y, r + R);
}

export interface HitTarget {
  hp: number;
  maxHp: number;
}

export interface HitResult {
  /** HP actually removed: the spell's damage capped at the target's remaining HP. */
  damage: number;
  /** Knockback impulse magnitude (units/s of slide velocity). */
  kb: number;
}

/** Multipliers on one hit (Stone Guard). Omitted = 1. */
export interface HitMods {
  /** Applied to the spell's damage before the remaining-HP cap. */
  damageMult?: number;
  /** Applied to the knockback after `computeKnockback` (so after the resist cap). */
  kbMult?: number;
}

/**
 * Damage and knockback for one hit. Knockback uses the target's HP *before* this hit, so the
 * client can preview it from the HP it displays. `knockbackMult` is the host setting; `resist` is
 * capped at KB_RESIST_CAP by `computeKnockback`. A non-finite `knockbackMult` counts as 1 and a
 * non-finite `resist` as 0, so `kb` is always finite. `mods` scale the result (Stone Guard: capped
 * resist plus a frontal guard is 0.5 × 0.35 = 17.5% of the base knockback).
 */
export function resolveHit(
  target: HitTarget,
  stats: { damage: number; force: number },
  knockbackMult: number,
  resist: number,
  mods: HitMods = {},
): HitResult {
  const hp = Math.max(0, target.hp);
  const damageMult = finiteMult(mods.damageMult);
  const damage = Math.min(Math.max(0, stats.damage) * damageMult, hp);
  const mult = Number.isFinite(knockbackMult) ? Math.max(0, knockbackMult) : 1;
  const force = stats.force * mult;
  const r = Number.isFinite(resist) ? resist : 0;
  return { damage, kb: computeKnockback(force, hp, target.maxHp, r) * finiteMult(mods.kbMult) };
}

/** A multiplier from optional input: absent or non-finite → 1, negative → 0. */
function finiteMult(m: number | undefined): number {
  return m !== undefined && Number.isFinite(m) ? Math.max(0, m) : 1;
}

export interface DamageEntry {
  sourceId: string;
  amount: number;
  /** Server clock ms. */
  at: number;
  /** The spell that dealt it, for the kill feed. */
  spellId?: SpellId;
}

/**
 * One victim's recent hits, oldest first. Lava and other sourceless damage are not recorded. A hit
 * with knockback but no damage (Snap, the Repulse nova) is recorded with amount 0.
 */
export type DamageLedger = DamageEntry[];

/**
 * Append a hit and drop entries too old to ever earn credit. A hit is recorded when it deals
 * damage or applies a knockback impulse (`knockback`, after resist and guard): in a game won by
 * pushing people into lava, a 0-damage push earns credit too (DROW-155 ruling). Hits with neither
 * are ignored; a non-finite or negative amount counts as 0.
 */
export function recordDamage(
  ledger: DamageLedger,
  sourceId: string,
  amount: number,
  at: number,
  spellId?: SpellId,
  knockback = 0,
): void {
  const dealt = Number.isFinite(amount) ? Math.max(0, amount) : 0;
  if (!(dealt > 0) && !(knockback > 0)) return;
  ledger.push(spellId ? { sourceId, amount: dealt, at, spellId } : { sourceId, amount: dealt, at });
  const cutoff = at - KILL_CREDIT_WINDOW_MS;
  const stale = ledger.findIndex((e) => e.at >= cutoff);
  if (stale > 0) ledger.splice(0, stale);
}

export interface KillCredit {
  killerId?: string;
  assistId?: string;
}

/**
 * GDD kill credit for a death at `now`: the killer is the source of the last recorded hit within
 * KILL_CREDIT_WINDOW_MS (a 0-damage push counts); the assist is whoever else dealt the most damage
 * in that window, ties (including all-zero pushes) going to the more recent hitter. No hit in the
 * window (e.g. a plain lava death) credits nobody.
 */
export function creditKill(ledger: readonly DamageEntry[], now: number): KillCredit {
  const cutoff = now - KILL_CREDIT_WINDOW_MS;
  const recent = ledger.filter((e) => e.at >= cutoff && e.at <= now && e.amount >= 0);
  let last: DamageEntry | undefined;
  for (const e of recent) if (!last || e.at >= last.at) last = e;
  if (!last) return {};
  const killerId = last.sourceId;

  const totals = new Map<string, { amount: number; lastAt: number }>();
  for (const e of recent) {
    if (e.sourceId === killerId) continue;
    const t = totals.get(e.sourceId);
    if (t) {
      t.amount += e.amount;
      t.lastAt = Math.max(t.lastAt, e.at);
    } else totals.set(e.sourceId, { amount: e.amount, lastAt: e.at });
  }
  let assistId: string | undefined;
  let best: { amount: number; lastAt: number } | undefined;
  for (const [id, t] of totals) {
    if (!best || t.amount > best.amount || (t.amount === best.amount && t.lastAt > best.lastAt)) {
      best = t;
      assistId = id;
    }
  }
  return assistId === undefined ? { killerId } : { killerId, assistId };
}

/** The spell of `killerId`'s most recent entry in the ledger (the kill-feed icon), if recorded. */
export function killingSpell(
  ledger: readonly DamageEntry[],
  killerId: string | undefined,
): SpellId | undefined {
  if (!killerId) return undefined;
  return ledger.findLast((e) => e.sourceId === killerId)?.spellId;
}

/**
 * The point a cast at (x, y) actually targets: unchanged within `range` of the caster, otherwise
 * pulled back onto the range circle along the same direction. Non-finite input targets the caster.
 */
export function clampCastTarget(caster: Vec2, x: number, y: number, range: number): Vec2 {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return { x: caster.x, y: caster.y };
  const dx = x - caster.x;
  const dy = y - caster.y;
  const dist = Math.hypot(dx, dy);
  const max = Math.max(0, range);
  if (dist <= max) return { x, y };
  const s = max / dist;
  return { x: caster.x + dx * s, y: caster.y + dy * s };
}

/**
 * HP fraction at or above which a warlock reads as healthy, and below which it reads as critical.
 * Missing HP scales knockback (KB_SCALING_K), so these mark roughly 1.4× and 1.7× knockback.
 */
export const HP_RISK_HURT = 0.6;
export const HP_RISK_CRITICAL = 0.3;

export type HpRisk = 'healthy' | 'hurt' | 'critical';

/**
 * How easy a warlock is to knock off, from plain HP%: the overhead bar's frame colour. No max HP
 * (not spawned yet) reads as healthy.
 */
export function hpRisk(hp: number, maxHp: number): HpRisk {
  if (!(maxHp > 0)) return 'healthy';
  const frac = hp / maxHp;
  if (frac >= HP_RISK_HURT) return 'healthy';
  if (frac >= HP_RISK_CRITICAL) return 'hurt';
  return 'critical';
}

/** Round the knockback readout to this step ("KB ×1.45"). */
export const KB_READOUT_STEP = 0.05;

/**
 * The knockback multiplier a warlock takes right now, before spell force and the host's lobby
 * multiplier: `(1 + k × missing HP) × (1 − resistance)`, rounded to KB_READOUT_STEP. The HUD's
 * "KB ×1.45" readout; `computeKnockback` with a base force of 1, so the two can't drift apart.
 */
export function knockbackReadout(hp: number, maxHp: number, resist: number): number {
  const mult = computeKnockback(1, hp, maxHp, resist);
  return Math.round(mult / KB_READOUT_STEP) * KB_READOUT_STEP;
}
